import { Link } from 'react-router-dom';
import { useState } from 'react';
import { siteConfig, navLinks, categories } from '@/mocks/siteData';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setStatusMsg('感谢订阅！');
      setEmail('');
      return;
    }
    try {
      const res = await fetch('https://form.readdy.ai/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore parse errors */ }
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMsg('感谢订阅！我们会将最新内容发送到您的邮箱。');
        setEmail('');
      } else {
        setStatus('error');
        setStatusMsg(parsed?.meta?.message || '订阅失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络错误，请稍后重试。');
    }
  };

  return (
    <footer className="bg-foreground-900 text-white" role="contentinfo">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="font-heading text-2xl font-bold text-white">{siteConfig.logo}</span>
            </Link>
            <p className="text-sm text-foreground-300 leading-relaxed mb-4">
              {siteConfig.description.substring(0, 80)}...
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer" aria-label="微博">
                <i className="ri-weibo-line text-sm"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer" aria-label="微信">
                <i className="ri-wechat-line text-sm"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer" aria-label="B站">
                <i className="ri-bilibili-line text-sm"></i>
              </a>
              <a href="#" className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer" aria-label="抖音">
                <i className="ri-tiktok-line text-sm"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">快速导航</h4>
            <ul className="space-y-2.5">
              {navLinks.filter(l => l.href !== '/').map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">热门分类</h4>
            <ul className="space-y-2.5">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link to={`/category/${cat.slug}`} className="text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">订阅更新</h4>
            <p className="text-sm text-foreground-300 mb-4 leading-relaxed">
              第一时间获取最新亚洲视频内容推荐，不错过任何精彩。
            </p>
            <form onSubmit={handleNewsletter} data-readdy-form="">
              <div className="form-honeypot">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入您的邮箱"
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-foreground-400 focus:outline-none focus:border-white/50 transition-colors"
                />
                <button
                  type="submit"
                  className="whitespace-nowrap px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  订阅
                </button>
              </div>
              {status !== 'idle' && (
                <p className={`text-xs mt-2 ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {statusMsg}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} {siteConfig.name}. 保留所有权利。
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-foreground-400 hover:text-white transition-colors cursor-pointer">隐私政策</a>
            <a href="#" className="text-xs text-foreground-400 hover:text-white transition-colors cursor-pointer">服务条款</a>
            <a href="#" className="text-xs text-foreground-400 hover:text-white transition-colors cursor-pointer">版权声明</a>
          </div>
        </div>
      </div>
    </footer>
  );
}